import { useState } from "react";

export default function AcceptInvite({ supabase, session }) {
  const user  = session?.user;
  const email = user?.email ?? "";
  const role  = user?.user_metadata?.role ?? "team member";

  const ROLE_LABEL = { admin: "Admin", manager: "Manager", view_only: "View Only", employee: "Employee" };

  const [fullName,  setFullName]  = useState(user?.user_metadata?.full_name ?? "");
  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [saving,    setSaving]    = useState(false);
  const [done,      setDone]      = useState(false);
  const [error,     setError]     = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!fullName.trim())       { setError("Please enter your full name."); return; }
    if (password.length < 8)    { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm)   { setError("Passwords do not match."); return; }

    setSaving(true);
    try {
      // 1 — Set password and display name
      const { error: updateErr } = await supabase.auth.updateUser({
        password,
        data: { full_name: fullName.trim() },
      });
      if (updateErr) throw new Error(updateErr.message);

      // 2 — Activate the team_members record (non-fatal if it fails;
      //     useAppData.loadAll() has a fallback activation on login)
      try {
        await supabase.functions.invoke("invite-team-member", {
          body: { action: "activate", name: fullName.trim() },
        });
      } catch (fnErr) {
        console.warn("AcceptInvite: activate step warning —", fnErr);
      }

      setDone(true);

      // Give the user a moment to read the success message, then reload cleanly
      setTimeout(() => {
        window.location.href = window.location.pathname; // strip the hash, restart app
      }, 2200);

    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const field = {
    background: "#FFFFFF", border: "1px solid #D4CFC7", borderRadius: 8,
    color: "#2B2B2B", padding: "11px 14px", fontSize: 15,
    fontFamily: "'Manrope', sans-serif", width: "100%",
    outline: "none", boxSizing: "border-box",
  };
  const label = {
    fontSize: 12, color: "#2B2B2B", fontFamily: "'Manrope', sans-serif",
    fontWeight: 600, marginBottom: 5, display: "block",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F5F1EA", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 16px", fontFamily: "'Manrope', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Logo */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}>
        <div style={{ width: 38, height: 38, background: "#163C2F", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: "#7FB98E", lineHeight: 1 }}>L</span>
        </div>
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 700, color: "#163C2F", letterSpacing: "0.01em" }}>Leasly</span>
      </div>

      {/* Card */}
      <div style={{ background: "#FFFFFF", border: "1px solid #E8E4DC", borderRadius: 16, padding: "36px 40px", width: "100%", maxWidth: 460, boxShadow: "0 2px 16px rgba(0,0,0,0.07)" }}>

        {done ? (
          // ── Success state ────────────────────────────────────────────────────
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ fontSize: 42, marginBottom: 16 }}>✅</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 26, color: "#163C2F", margin: "0 0 10px" }}>
              You're all set!
            </h2>
            <p style={{ fontSize: 14, color: "#9B9690", lineHeight: 1.6, margin: 0 }}>
              Your account is ready. Taking you to the dashboard…
            </p>
          </div>
        ) : (
          // ── Setup form ───────────────────────────────────────────────────────
          <>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 28, color: "#2B2B2B", margin: "0 0 6px" }}>
              Set up your account
            </h1>
            <p style={{ fontSize: 13, color: "#9B9690", margin: "0 0 24px", lineHeight: 1.6 }}>
              You've been invited to join the dashboard as a{" "}
              <strong style={{ color: "#163C2F" }}>{ROLE_LABEL[role] ?? role}</strong>.
              Complete your profile to get started.
            </p>

            {/* Invited-as chip */}
            <div style={{ background: "#F5F1EA", borderRadius: 8, padding: "10px 14px", marginBottom: 24, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13 }}>✉️</span>
              <span style={{ fontSize: 13, color: "#2B2B2B" }}>Signing in as <strong>{email}</strong></span>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <div>
                <label style={label}>Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  style={field}
                  placeholder="e.g. Jordan Smith"
                  autoFocus
                  required
                />
              </div>

              <div>
                <label style={label}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={field}
                  placeholder="At least 8 characters"
                  required
                />
              </div>

              <div>
                <label style={label}>Confirm Password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  style={field}
                  placeholder="Re-enter your password"
                  required
                />
              </div>

              {error && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626" }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                style={{
                  background: saving ? "#9B9690" : "#163C2F",
                  color: "#F5F1EA", border: "none", borderRadius: 999,
                  padding: "13px 0", fontFamily: "'Manrope', sans-serif",
                  fontSize: 13, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
                  textTransform: "uppercase", letterSpacing: "0.08em",
                  transition: "background 0.15s",
                }}
              >
                {saving ? "Setting up…" : "Complete Setup"}
              </button>
            </form>
          </>
        )}
      </div>

      <p style={{ marginTop: 24, fontSize: 12, color: "#B0ACA6", textAlign: "center" }}>
        Having trouble? Contact your organisation admin.
      </p>
    </div>
  );
}
